/// <reference path="../groupthink-js.d.ts" />

import { axios, fetcher, apiRequest } from '../lib';
import useSWR, { mutate } from 'swr';
import { AxiosRequestConfig } from 'axios';

export const useThread = (agendaId?: string, id?: string) => {
  const {
    data: thread,
    error,
    isLoading,
    mutate,
  } = useSWR<Groupthink.SuccessfulResponseContent<'thread.show'>>(
    () => (Boolean(agendaId) && Boolean(id) ? `/v1/agendas/${agendaId}/threads/${id}` : false),
    fetcher,
    { keepPreviousData: true }
  );

  const markThreadAsRead = <RouteName = 'thread.mark_as_read'>(
    message_id: string,
    {
      setErrors,
      setIsMarkingRead,
      onSuccess,
    }: Omit<Groupthink.BaseOperationOptions<RouteName>, 'payload'> & {
      setIsMarkingRead?: (isMarkingRead: boolean) => void;
    }
  ) =>
    apiRequest<RouteName>(`/v1/agendas/${agendaId}/threads/${id}/mark_as_read`, mutate, 'POST', {
      setErrors,
      setLoading: setIsMarkingRead,
      payload: { message_id },
      onSuccess,
    });

  return {
    thread: thread?.data,
    isLoading,
    isError: error,
    mutate,
    markThreadAsRead,
  };
};

export const useThreads = (agendaId?: string) => {
  const {
    data: thread,
    error,
    isLoading,
    mutate,
  } = useSWR<Groupthink.SuccessfulResponseContent<'thread.index'>>(
    () => (agendaId ? `/v1/agendas/${agendaId}/threads` : false),
    fetcher,
    {
      keepPreviousData: true,
    }
  );

  const createThread = <RouteName = 'thread.store'>({
    setErrors,
    setIsCreating,
    onSuccess,
    payload,
  }: Groupthink.CreateOperationOptions<RouteName>) =>
    apiRequest<RouteName>(`/v1/agendas/${agendaId}/threads`, mutate, 'POST', {
      setErrors,
      setLoading: setIsCreating,
      payload,
      onSuccess,
    });

  const updateThread = <RouteName = 'thread.update'>(
    threadId: string,
    { setErrors, setIsCreating, onSuccess, payload }: Groupthink.CreateOperationOptions<RouteName>
  ) =>
    apiRequest<RouteName>(`/v1/agendas/${agendaId}/threads/${threadId}`, mutate, 'PUT', {
      setErrors,
      setLoading: setIsCreating,
      payload,
      onSuccess,
    });

  const deleteThread = async <RouteName = 'thread.destroy'>(
    threadId: string,
    { setErrors, setIsDeleting, onSuccess }: Groupthink.DeleteOperationOptions<RouteName>
  ) =>
    apiRequest<RouteName>(`/v1/agendas/${agendaId}/threads/${threadId}`, mutate, 'DELETE', {
      setErrors,
      setLoading: setIsDeleting,
      onSuccess,
    });

  return {
    threads: thread?.data,
    isLoading,
    isError: error,
    mutate,
    createThread,
    updateThread,
    deleteThread,
  };
};

export const manipulateThreads = async <RouteName>(
  url: string,
  {
    method = 'POST',
    payload,
  }: {
    method?: AxiosRequestConfig['method'];
    payload?: Groupthink.RequestPayload<RouteName> | Record<string, unknown>;
  }
) => {
  await axios(url, {
    method,
    data: payload,
  });
};

export const markThreadAsRead = (agendaId: string, id: string, message_id: string) =>
  mutate(
    (key) => typeof key === 'string' && key.startsWith(`/v1/agendas`),
    manipulateThreads(`/v1/agendas/${agendaId}/threads/${id}/mark_as_read`, {
      method: 'POST',
      payload: { message_id },
    })
  );
